import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Target, ArrowsClockwise, Trophy, Users, ChartBar, Clock,
  ArrowRight, CheckCircle, WarningCircle, Lock,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import StrategyModeBanner from "@/components/StrategyModeBanner";
import PortfolioSplit from "@/components/PortfolioSplit";
import ContestSimulator from "@/components/ContestSimulator";
import EntryTemplate from "@/components/EntryTemplate";
import WarRoomWalkthrough from "@/components/WarRoomWalkthrough";
import { PlayCircle } from "@phosphor-icons/react";

const AUTO_SWAP_INTERVAL_MS = 60_000;   // 1 min poll when auto-monitor is on

export default function WarRoom() {
  const [walkOpen, setWalkOpen] = useState(false);
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Simulation</div>
          <h1 className="heading text-3xl font-black uppercase tracking-tighter mt-1">
            Contest Simulation
          </h1>
          <p className="text-zinc-300 text-sm mt-2 max-w-2xl">
            Tournament sizing math + Monte-Carlo payout simulation + dual-batch portfolio split. Runs against your current lineup portfolio. (Late Swap moved to its own tab.)
          </p>
        </div>
        <button
          onClick={() => setWalkOpen(true)}
          data-testid="open-walkthrough-btn"
          className="inline-flex items-center gap-2 border-2 border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors font-black uppercase tracking-widest text-xs py-2.5 px-4 flex-shrink-0"
          style={{ boxShadow: "0 0 24px rgba(57,255,20,0.15)" }}
        >
          <PlayCircle size={14} weight="fill" />
          How It Works · Watch
        </button>
      </div>

      {/* Sprint 60.100 — EntryTemplate (Contest Ops multi-contest CSV
          routing) removed from Simulation. That workflow is now
          available via the Manual Optimizer's DK CSV upload/download flow;
          keeping it here duplicated confusion. LateSwapSection also
          removed — Late Swap has its own dedicated tab now. */}
      <ContestSizingSection />
      <ContestSimulator />
      <PortfolioSplit />
      <WarRoomWalkthrough open={walkOpen} onClose={() => setWalkOpen(false)} />
    </div>
  );
}

// ================================================================
//                       CONTEST SIZING
// ================================================================

function ContestSizingSection() {
  const [presets, setPresets] = useState({});
  const [preset, setPreset] = useState(
    (typeof localStorage !== "undefined" && localStorage.getItem("wp_active_preset")) || "milly_maker"
  );
  const [fieldSize, setFieldSize] = useState(200000);
  const [maxEntries, setMaxEntries] = useState(150);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.contestPresets().then((d) => {
      setPresets(d.presets || {});
      // Sync sliders to whatever preset is currently active
      const p = (d.presets || {})[preset];
      if (p) { setFieldSize(p.field_size); setMaxEntries(p.max_entries_per_user); }
    }).catch((e) => console.debug("[WarRoom] contestPresets fetch failed:", e?.message || e));
  }, []);

  // React to preset broadcasts (Kickoff Countdown tabs, Optimizer picker)
  useEffect(() => {
    const onPresetChange = (e) => {
      const next = e?.detail?.preset;
      if (!next || next === preset) return;
      setPreset(next);
      const p = presets[next];
      if (p) { setFieldSize(p.field_size); setMaxEntries(p.max_entries_per_user); }
    };
    window.addEventListener("wp:preset-change", onPresetChange);
    return () => window.removeEventListener("wp:preset-change", onPresetChange);
  }, [presets, preset]);

  const applyPreset = (key) => {
    setPreset(key);
    try { localStorage.setItem("wp_active_preset", key); }
    catch (e) { console.debug("localStorage.setItem failed (private mode?):", e?.message); }
    try {
      window.dispatchEvent(new CustomEvent("wp:preset-change", { detail: { preset: key, source: "warroom" } }));
    } catch (e) { console.debug("preset-change dispatch failed:", e?.message); }
    const p = presets[key];
    if (p) {
      setFieldSize(p.field_size);
      setMaxEntries(p.max_entries_per_user);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const d = await api.contestSizing({
        field_size: Number(fieldSize),
        max_entries_per_user: Number(maxEntries),
      });
      setReport(d);
      toast.success(`Contest analysis complete: ${d.portfolio_size} lineups vs ${d.field_size.toLocaleString()} entries`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Contest sizing failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-[#121212] border border-zinc-800">
      <div className="p-4 border-b border-zinc-800">
        <StrategyModeBanner presets={presets} activeKey={preset} />
      </div>
      <header className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Target size={20} className="text-[#39FF14]" weight="fill" />
          <div>
            <div className="overline">Contest Sizing Calculator</div>
            <div className="text-sm text-zinc-300">
              Duplication rate, Milly hit %, recommended entries
            </div>
          </div>
        </div>
        <button
          data-testid={TID.contestRunBtn}
          onClick={run}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2ee30d] text-black font-bold uppercase tracking-widest text-xs py-2.5 px-5 transition-colors disabled:opacity-50"
        >
          <ChartBar size={14} weight="bold" />
          {busy ? "Running..." : "Run Analysis"}
        </button>
      </header>

      <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
        <label className="block">
          <div className="overline mb-1">Contest Preset</div>
          <select
            data-testid={TID.contestPresetSelect}
            value={preset}
            onChange={(e) => applyPreset(e.target.value)}
            className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm mono"
          >
            {Object.entries(presets).map(([k, v]) => (
              <option key={k} value={k}>
                {`${v.label || k.replace(/_/g, " ").toUpperCase()} — ${v.field_size.toLocaleString()}`}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <div className="overline mb-1">Field Size</div>
          <input
            data-testid={TID.contestFieldSize}
            type="number"
            value={fieldSize}
            onChange={(e) => setFieldSize(e.target.value)}
            className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm mono"
          />
        </label>

        <label className="block">
          <div className="overline mb-1">Max Entries / User</div>
          <input
            data-testid={TID.contestMaxEntries}
            type="number"
            value={maxEntries}
            onChange={(e) => setMaxEntries(e.target.value)}
            className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm mono"
          />
        </label>

        <div className="text-xs text-zinc-500 leading-relaxed self-end">
          <div>
            <span className="text-[#39FF14]">Milly Maker</span>: 200k field, top prize {"~"}$1M
          </div>
          <div>Uses your last generated portfolio</div>
        </div>
      </div>

      {report && (
        <div data-testid={TID.contestReport} className="border-t border-zinc-800">
          {report.narrative && (
            <div
              data-testid="contest-narrative"
              className="px-5 py-3 border-b border-zinc-800"
              style={{
                borderLeftWidth: 4,
                borderLeftColor: report.strategy_mode === "small" ? "#39FF14" :
                                 report.strategy_mode === "mid" ? "#FFCC00" : "#FF3B30",
                borderLeftStyle: "solid",
              }}
            >
              <span className="text-[11px] text-zinc-300 leading-relaxed">{report.narrative}</span>
            </div>
          )}
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 border-b border-zinc-800">
            <MetricCard label="Portfolio" value={report.portfolio_size} />
            <MetricCard label="Recommended" value={report.recommended_entries} />
            {report.strategy_mode === "small" ? (
              <>
                <MetricCard label="Exp Cashes" value={report.portfolio_expected_cashes} accent />
                <MetricCard label="Avg Floor" value={report.avg_floor} accent />
                <MetricCard label="Avg Cash %" value={`${report.avg_cash_hit_pct}%`} />
                <MetricCard label="Avg Ceil" value={report.avg_ceiling} />
              </>
            ) : (
              <>
                <MetricCard label="Unique %" value={`${report.unique_pct}%`} accent />
                <MetricCard
                  label="Milly Hit"
                  value={`${report.portfolio_milly_hit_prob}%`}
                  accent={report.portfolio_milly_hit_prob > 5}
                />
                <MetricCard label="Exp Cashes" value={report.portfolio_expected_cashes} />
                <MetricCard label="Avg Dupes" value={report.avg_expected_dupes} warn={report.avg_expected_dupes > 5} />
              </>
            )}
          </div>

          <div className="max-h-[500px] overflow-y-auto">
            <table className="w-full text-xs mono">
              <thead className="bg-black sticky top-0">
                <tr className="text-left text-zinc-300 uppercase tracking-widest text-[10px]">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Proj</th>
                  <th className="px-3 py-2">Ceil</th>
                  <th className="px-3 py-2">Floor</th>
                  <th className="px-3 py-2">Own %</th>
                  <th className="px-3 py-2" title="Predicted duplicate lineups in field">Dupes</th>
                  <th className="px-3 py-2" title="Predicted finish percentile (higher = better)">Finish %</th>
                  <th className="px-3 py-2" title="% chance of hitting Milly winner score">Milly %</th>
                  <th className="px-3 py-2" title="% chance of cashing">Cash %</th>
                </tr>
              </thead>
              <tbody>
                {report.lineups.map((L) => (
                  <tr key={L.lineup_id} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                    <td className="px-3 py-2 font-bold">{L.lineup_id}</td>
                    <td className="px-3 py-2 text-[#39FF14]">{L.projection?.toFixed?.(1)}</td>
                    <td className="px-3 py-2 text-[#39FF14]">{L.ceiling?.toFixed?.(1)}</td>
                    <td className="px-3 py-2 text-zinc-500">{L.floor?.toFixed?.(1)}</td>
                    <td className="px-3 py-2">{L.total_own?.toFixed?.(1)}%</td>
                    <td className={`px-3 py-2 ${L.expected_dupes > 5 ? "text-[#FF3B30]" : L.expected_dupes > 1 ? "text-[#FFCC00]" : "text-[#39FF14]"}`}>
                      {L.expected_dupes}
                    </td>
                    <td className="px-3 py-2">{L.finish_percentile}%</td>
                    <td className={`px-3 py-2 ${L.milly_hit_pct > 5 ? "text-[#39FF14] font-bold" : "text-zinc-300"}`}>
                      {L.milly_hit_pct}%
                    </td>
                    <td className="px-3 py-2 text-zinc-300">{L.cash_hit_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

// ================================================================
//                        LATE SWAP
// ================================================================

function LateSwapSection() {
  const [games, setGames] = useState({});
  const [lateGames, setLateGames] = useState({});      // { gameId: true }
  const [scores, setScores] = useState({});            // { dk_id: score }
  const [pool, setPool] = useState([]);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoMonitor, setAutoMonitor] = useState(false);
  const [lastAutoRun, setLastAutoRun] = useState(null);

  const load = async () => {
    try {
      const [g, p] = await Promise.all([api.lateSwapGames(), api.getPool(500)]);
      setGames(g.games || {});
      setPool(p.rows || []);
    } catch (e) {
      console.debug("late swap games/pool load failed:", e?.message || e);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-monitor: re-run analysis every N seconds if enabled
  useEffect(() => {
    if (!autoMonitor) return;
    const t = setInterval(() => {
      runAnalysis(true);
    }, AUTO_SWAP_INTERVAL_MS);
    return () => clearInterval(t);
  }, [autoMonitor, lateGames, scores]); // eslint-disable-line

  const toggleGame = (gid) => {
    setLateGames((prev) => ({ ...prev, [gid]: !prev[gid] }));
  };

  const setScore = (dkId, val) => {
    setScores((prev) => ({ ...prev, [dkId]: val }));
  };

  const buildSwappable = () => {
    const ids = [];
    Object.entries(games).forEach(([gid, playerIds]) => {
      if (lateGames[gid]) ids.push(...playerIds);
    });
    return ids;
  };

  const runAnalysis = async (silent = false) => {
    const late_swappable_dk_ids = buildSwappable();
    if (late_swappable_dk_ids.length === 0) {
      if (!silent) toast.warning("Select at least one late game");
      return;
    }
    const realized_scores = {};
    Object.entries(scores).forEach(([k, v]) => {
      const n = parseFloat(v);
      if (!isNaN(n)) realized_scores[k] = n;
    });

    setBusy(true);
    try {
      const d = await api.lateSwapAnalyze({ realized_scores, late_swappable_dk_ids });
      setReport(d);
      setLastAutoRun(new Date().toISOString());
      if (!silent) {
        toast.success(`${d.high_priority_count} HIGH priority swaps, ${d.portfolio_size} lineups analyzed`);
      } else if (d.high_priority_count > 0) {
        toast.warning(`⚠️ ${d.high_priority_count} HIGH priority swaps available (auto-monitor)`, {
          duration: 6000,
        });
      }
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Late swap failed");
    } finally {
      setBusy(false);
    }
  };

  // Group pool by dk_id for quick lookup
  const poolMap = React.useMemo(() => {
    const m = {};
    pool.forEach((p) => { m[String(p.dk_id)] = p; });
    return m;
  }, [pool]);

  const anyGameSelected = Object.values(lateGames).some(Boolean);
  const scoredCount = Object.keys(scores).filter((k) => scores[k] !== "").length;

  // Memoize the sorted game list so we don't re-sort on every render / score change
  const gameIds = React.useMemo(() => Object.keys(games).sort(), [games]);

  return (
    <section className="bg-[#121212] border border-zinc-800 relative">
      <header className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Clock size={20} className="text-[#FFCC00]" weight="fill" />
          <div>
            <div className="overline">Late Swap Advisor</div>
            <div className="text-sm text-zinc-300">
              Sunday PM re-solve using early game realized scores
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs mono text-zinc-300 cursor-pointer">
            <input
              data-testid={TID.lateSwapAutoToggle}
              type="checkbox"
              checked={autoMonitor}
              onChange={(e) => setAutoMonitor(e.target.checked)}
              className="accent-[#39FF14]"
            />
            AUTO-MONITOR (60s)
          </label>
          <button
            data-testid={TID.lateSwapRunBtn}
            onClick={() => runAnalysis(false)}
            disabled={busy || !anyGameSelected}
            className="inline-flex items-center gap-2 bg-[#FFCC00] hover:bg-[#e6b800] text-black font-bold uppercase tracking-widest text-xs py-2.5 px-5 transition-colors disabled:opacity-50"
          >
            <ArrowsClockwise size={14} weight="bold" />
            {busy ? "Analyzing..." : "Run Late Swap"}
          </button>
        </div>
      </header>

      {/* Lock overlay: when no DK pool is loaded, the advisor's inputs are
          useless — show a locked overlay explaining exactly when the tool
          activates instead of exposing broken-looking empty fields. */}
      {Object.keys(games).length === 0 && (
        <div
          data-testid="late-swap-locked"
          className="absolute inset-x-0 bottom-0 top-[73px] z-10 bg-[#0a0a0a]/95 backdrop-blur-sm flex flex-col items-center justify-center p-8 border-t border-zinc-800"
        >
          <div className="w-14 h-14 flex items-center justify-center bg-[#FFCC00]/10 border border-[#FFCC00]/40 mb-4">
            <Lock size={22} className="text-[#FFCC00]" weight="fill" />
          </div>
          <div className="heading text-xl font-bold text-white mb-2">Advisor unlocks after kickoff</div>
          <div className="text-sm text-zinc-300 text-center max-w-lg leading-relaxed">
            Late Swap uses <span className="text-[#39FF14]">live realized fantasy scores</span> from early games to
            re-solve your late-slate portfolio. Upload your DK slate in{" "}
            <span className="text-[#39FF14]">Slate Selection</span> — the advisor auto-activates 15 minutes after main-slate lock.
          </div>
        </div>
      )}

      <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: game selector */}
        <div>
          <div className="overline mb-2">1. Mark Late Games (swap-eligible)</div>
          <div className="grid grid-cols-2 gap-2">
            {Object.keys(games).length === 0 && (
              <div className="text-zinc-500 text-xs col-span-2">Upload DK pool first.</div>
            )}
            {gameIds.map((gid) => {
              const active = lateGames[gid];
              return (
                <button
                  key={gid}
                  onClick={() => toggleGame(gid)}
                  className={`px-3 py-2 text-xs mono border transition-colors ${
                    active
                      ? "bg-[#FFCC00]/15 border-[#FFCC00] text-[#FFCC00]"
                      : "bg-black border-zinc-800 text-zinc-300 hover:border-zinc-700"
                  }`}
                >
                  {active && <CheckCircle size={10} className="inline mr-1" weight="fill" />}
                  {gid}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: realized scores input */}
        <div>
          <div className="overline mb-2">
            2. Enter Realized Fantasy Scores <span className="text-zinc-600">({scoredCount} entered)</span>
          </div>
          <ScoresInput pool={pool} scores={scores} onSetScore={setScore} />
        </div>
      </div>

      {autoMonitor && lastAutoRun && (
        <div className="px-5 py-2 border-t border-zinc-800 text-[10px] mono text-zinc-500">
          Last auto-check: {new Date(lastAutoRun).toLocaleTimeString()}
        </div>
      )}

      {report && (
        <div data-testid={TID.lateSwapReport} className="border-t border-zinc-800">
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-zinc-800">
            <MetricCard label="Portfolio" value={report.portfolio_size} />
            <MetricCard
              label="High Priority"
              value={report.high_priority_count}
              warn={report.high_priority_count > 0}
            />
            <MetricCard label="Avg Current" value={report.avg_current_total} />
            <MetricCard label="Avg Ceiling" value={report.avg_ceiling_total} accent />
          </div>

          <div className="max-h-[500px] overflow-y-auto">
            {report.analyses.map((a) => (
              <div key={a.lineup_id} className="border-t border-zinc-900 px-5 py-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-sm">Lineup {a.lineup_id}</span>
                    <PriorityBadge priority={a.swap_priority} />
                    <span className="text-xs mono text-zinc-300">
                      Locked: <span className="text-[#39FF14]">{a.locked_actual}</span> · Ceiling total: <span className="text-[#39FF14]">{a.ceiling_total}</span>
                    </span>
                  </div>
                  <span className="text-xs mono text-zinc-500">
                    Top swap gain: <span className="text-[#39FF14] font-bold">+{a.top_swap_gain}</span>
                  </span>
                </div>
                {a.candidates.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {a.candidates.slice(0, 5).map((c) => (
                      <div key={`${a.lineup_id}-${c.swap_out_dk_id || c.swap_out_name}-${c.swap_in_dk_id || c.swap_in_name}`} className="flex items-center justify-between text-xs mono border border-zinc-900 px-3 py-2 hover:bg-zinc-900/40">
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-[10px] uppercase text-zinc-500 w-8">{c.position}</span>
                          <span className="text-zinc-300">{c.swap_out_name}</span>
                          <span className="text-zinc-600 mx-1 text-[10px]">({c.swap_out_proj})</span>
                          <ArrowRight size={12} className="text-[#FFCC00] mx-1" />
                          <span className="text-white font-medium">{c.swap_in_name}</span>
                          <span className="text-[#39FF14] mx-1">({c.swap_in_proj})</span>
                          <span className="text-[10px] text-zinc-500">own {c.swap_in_own}%</span>
                        </div>
                        <div className="text-right">
                          <div className="text-[#39FF14] font-bold">+{c.proj_gain}</div>
                          <div className="text-[10px] text-zinc-500">
                            {c.salary_delta >= 0 ? "+" : ""}{c.salary_delta}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[10px] text-zinc-600 mt-2">No swaps available</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function ScoresInput({ pool, scores, onSetScore }) {
  const [query, setQuery] = useState("");
  const filtered = pool
    .filter((p) => !query || p.name?.toLowerCase().includes(query.toLowerCase()) || p.team?.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 20);

  return (
    <div>
      <input
        type="text"
        placeholder="Search player..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm mono mb-2"
      />
      <div className="max-h-64 overflow-y-auto border border-zinc-900">
        {filtered.map((p) => (
          <div key={p.dk_id} className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-900 hover:bg-zinc-900/40 text-xs mono">
            <div className="flex-1">
              <span className="text-[10px] uppercase text-zinc-500 w-8 inline-block">{p.position}</span>
              <span>{p.name}</span>
              <span className="text-zinc-500 ml-2">{p.team}</span>
            </div>
            <input
              type="number"
              step="0.1"
              placeholder="—"
              value={scores[String(p.dk_id)] || ""}
              onChange={(e) => onSetScore(String(p.dk_id), e.target.value)}
              className="w-16 bg-black border border-zinc-800 px-2 py-1 text-right text-xs"
            />
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-zinc-600 text-xs px-3 py-4">No players match.</div>
        )}
      </div>
    </div>
  );
}

// ================================================================
//                            SHARED
// ================================================================

function MetricCard({ label, value, accent, warn }) {
  const color = accent ? "text-[#39FF14]" : warn ? "text-[#FF3B30]" : "text-white";
  return (
    <div className="bg-black border border-zinc-800 px-3 py-3">
      <div className="overline text-[9px]">{label}</div>
      <div className={`heading text-2xl font-black mt-1 mono ${color}`}>{value}</div>
    </div>
  );
}

function PriorityBadge({ priority }) {
  const map = {
    HIGH: "bg-[#FF3B30] text-white",
    MEDIUM: "bg-[#FFCC00] text-black",
    LOW: "bg-zinc-800 text-zinc-300",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase font-bold tracking-widest ${map[priority] || map.LOW}`}>
      {priority === "HIGH" && <WarningCircle size={10} weight="fill" />}
      {priority}
    </span>
  );
}
